import { useEffect } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogType?: string;
  ogImage?: string;
  schema?: object | object[];
}

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const SEOHead = ({
  title,
  description,
  keywords,
  canonical,
  ogType = 'website',
  ogImage = `${SITE_URL}/og-image.jpg`,
  schema,
}: SEOHeadProps) => {
  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string, attr = 'name') => {
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
    };

    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);
    setMeta('robots', 'index, follow');

    const canonicalUrl = canonical ? `${SITE_URL}${canonical}` : SITE_URL;
    setLink('canonical', canonicalUrl);

    // Open Graph
    setMeta('og:type', ogType, 'property');
    setMeta('og:title', title, 'property');
    setMeta('og:description', description, 'property');
    setMeta('og:url', canonicalUrl, 'property');
    setMeta('og:image', ogImage, 'property');

    // Twitter
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    setMeta('twitter:image', ogImage);

    // Schema JSON-LD
    const existingSchema = document.querySelectorAll('script[data-seo-schema]');
    existingSchema.forEach((el) => el.remove());

    if (schema) {
      const schemas = Array.isArray(schema) ? schema : [schema];
      schemas.forEach((s) => {
        const scriptEl = document.createElement('script');
        scriptEl.setAttribute('type', 'application/ld+json');
        scriptEl.setAttribute('data-seo-schema', 'true');
        scriptEl.textContent = JSON.stringify(s);
        document.head.appendChild(scriptEl);
      });
    }

    return () => {
      const cleanup = document.querySelectorAll('script[data-seo-schema]');
      cleanup.forEach((el) => el.remove());
    };
  }, [title, description, keywords, canonical, ogType, ogImage, schema]);

  return null;
};

export default SEOHead;
